// --------------------------- ANNOUNCEMENT BAR MARQUEE COMPONENT ---------------------------

class AnnouncementMarquee extends HTMLElement {
  static get observedAttributes() {
    return ['data-mode', 'data-speed', 'data-fade-interval', 'data-pause-hover'];
  }

  connectedCallback() {
    this._ac = new AbortController();
    this._viewport = this.querySelector('.am-viewport');
    this._mode = this.dataset.mode || 'marquee';

    if (this._mode === 'marquee') {
      this._initMarquee();
    } else {
      this._initFade();
    }

    this._bindCloseButton();
    this._bindThemeEditorEvents();
  }

  disconnectedCallback() {
    this._cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== null && oldValue !== newValue) {
      this._cleanup();
      this._mode = this.dataset.mode || 'marquee';
      if (this._mode === 'marquee') {
        this._initMarquee();
      } else {
        this._initFade();
      }
    }
  }

  _cleanup() {
    this._ac?.abort();
    this._ac = new AbortController();

    if (this._fadeTimer) {
      clearInterval(this._fadeTimer);
      this._fadeTimer = null;
    }

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }

    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  /* ── Marquee Mode ─────────────────────────────────────────── */

  _initMarquee() {
    const track = this.querySelector('.am-marquee-track');
    if (!track) return;

    const originalGroup = track.querySelector('[data-original-group]');
    if (!originalGroup) return;

    const buildSeamlessTrack = () => {
      // Clean any previously generated duplicate groups or clones
      track.querySelectorAll('.am-group:not([data-original-group])').forEach((el) => el.remove());
      originalGroup.querySelectorAll('[data-item-clone]').forEach((el) => el.remove());

      const originalNodes = [...originalGroup.children].filter(
        (el) => !el.hasAttribute('data-item-clone')
      );
      if (!originalNodes.length) return;

      const viewportWidth = this._viewport ? this._viewport.clientWidth : this.clientWidth;
      let groupWidth = originalGroup.scrollWidth;

      // If the content is narrower than the viewport, duplicate items inside the original group
      // so there are no empty gaps visible during infinite scroll
      if (viewportWidth > 0 && groupWidth > 0) {
        let safetyCounter = 0;
        while (groupWidth < viewportWidth && safetyCounter < 10) {
          originalNodes.forEach((node) => {
            const nodeClone = node.cloneNode(true);
            nodeClone.setAttribute('data-item-clone', 'true');
            nodeClone.setAttribute('aria-hidden', 'true');
            originalGroup.appendChild(nodeClone);
          });
          groupWidth = originalGroup.scrollWidth;
          safetyCounter++;
        }
      }

      // Create a second identical group to follow the first group continuously
      const duplicateGroup = originalGroup.cloneNode(true);
      duplicateGroup.removeAttribute('data-original-group');
      duplicateGroup.setAttribute('aria-hidden', 'true');
      track.appendChild(duplicateGroup);

      // Configure animation duration from setting or proportional width
      const configuredSpeed = parseFloat(this.dataset.speed) || 25;
      this.style.setProperty('--am-duration', `${configuredSpeed}s`);
    };

    // Initial build
    buildSeamlessTrack();

    // Recompute on window resize to ensure seamless loop remains gapless
    if ('ResizeObserver' in window && this._viewport) {
      this._resizeObserver = new ResizeObserver(() => {
        if (this._rafId) cancelAnimationFrame(this._rafId);
        this._rafId = requestAnimationFrame(() => buildSeamlessTrack());
      });
      this._resizeObserver.observe(this._viewport);
    }
  }

  /* ── Fade Mode ────────────────────────────────────────────── */

  _initFade() {
    this._fadeItems = [...this.querySelectorAll('.am-item--fade')];
    if (this._fadeItems.length <= 1) return;

    this._currentIndex = 0;
    const intervalMs = (parseFloat(this.dataset.fadeInterval) || 4) * 1000;

    const startTimer = () => {
      if (this._fadeTimer) clearInterval(this._fadeTimer);
      this._fadeTimer = setInterval(() => this._nextFadeItem(), intervalMs);
    };

    const stopTimer = () => {
      if (this._fadeTimer) {
        clearInterval(this._fadeTimer);
        this._fadeTimer = null;
      }
    };

    // Pause on hover if setting enabled
    if (this.dataset.pauseHover === 'true') {
      const { signal } = this._ac;
      this.addEventListener('mouseenter', stopTimer, { signal });
      this.addEventListener('mouseleave', startTimer, { signal });
    }

    startTimer();
  }

  _setActiveFadeItem(targetIndex) {
    if (!this._fadeItems || !this._fadeItems.length) return;

    this._fadeItems.forEach((item, index) => {
      const isActive = index === targetIndex;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this._currentIndex = targetIndex;
  }

  _nextFadeItem() {
    const nextIndex = (this._currentIndex + 1) % this._fadeItems.length;
    this._setActiveFadeItem(nextIndex);
  }

  /* ── Close Button ─────────────────────────────────────────── */

  _bindCloseButton() {
    const closeBtn = this.querySelector('.am-close-btn');
    if (!closeBtn) return;

    const { signal } = this._ac;

    closeBtn.addEventListener(
      'click',
      (e) => {
        e.preventDefault();
        this._dismiss();
      },
      { signal }
    );
  }

  _dismiss() {
    // Smooth collapse with zero CLS impact
    const currentHeight = this.offsetHeight;
    this.style.transition = 'max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease, min-height 0.25s ease';
    this.style.maxHeight = `${currentHeight}px`;
    this.style.minHeight = '0px';

    requestAnimationFrame(() => {
      this.style.maxHeight = '0px';
      this.style.opacity = '0';
      this.style.pointerEvents = 'none';

      setTimeout(() => {
        this.hidden = true;
        this.classList.add('is-dismissed');
        this.dispatchEvent(
          new CustomEvent('announcement:dismissed', {
            bubbles: true,
            composed: true,
          })
        );
      }, 250);
    });
  }

  /* ── Shopify Theme Editor Integration ─────────────────────── */

  _bindThemeEditorEvents() {
    const { signal } = this._ac;

    // Direct block selection in theme editor
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        if (!this.contains(event.target)) return;

        if (this._mode === 'fade') {
          if (this._fadeTimer) clearInterval(this._fadeTimer);
          const targetItem = event.target.closest('.am-item--fade');
          if (targetItem && this._fadeItems) {
            const index = this._fadeItems.indexOf(targetItem);
            if (index !== -1) this._setActiveFadeItem(index);
          }
        }
      },
      { signal }
    );

    document.addEventListener(
      'shopify:block:deselect',
      (event) => {
        if (!this.contains(event.target)) return;

        if (this._mode === 'fade') {
          const intervalMs = (parseFloat(this.dataset.fadeInterval) || 4) * 1000;
          if (this._fadeTimer) clearInterval(this._fadeTimer);
          this._fadeTimer = setInterval(() => this._nextFadeItem(), intervalMs);
        }
      },
      { signal }
    );
  }
}

if (!customElements.get('announcement-marquee')) {
  customElements.define('announcement-marquee', AnnouncementMarquee);
}


// --------------------------- USP GRID SLIDER COMPONENT ---------------------------
class USPGrid extends HTMLElement {
  constructor() {
    super()

    this.track = this.querySelector(".usp-grid-track")
    this.blocks = this.querySelectorAll(".block_content")
    this.dots = this.querySelectorAll(".grid-pagination .dot")
    this.isScrolling = false

    if (window.innerWidth < 750) {
      this._initSlider()
    }
  }

  _initSlider() {
    if (!this.track || !this.blocks.length || !this.dots.length) return

    this.track.addEventListener("scroll", () => {
      if (this.isScrolling) return

      this.isScrolling = true

      requestAnimationFrame(() => {
        this.isScrolling = false

        const maxScroll = this.track.scrollWidth - this.track.clientWidth
        const index = Math.round(
          (this.track.scrollLeft / maxScroll) * (this.blocks.length - 1)
        )

        this.dots.forEach((dot, i) => {
          dot.classList.toggle("active", i === index)
        })
      })
    })
  }
}

customElements.define("usp-grid", USPGrid)


// --------------------------- CUSTOM CARD SLIDER COMPONENT ---------------------------
class CardSlider extends HTMLElement {

  static get observedAttributes() {
    return ['cards-desktop', 'cards-mobile', 'gap'];
  }

  connectedCallback() {
    // Register the "init-slider" custom event listener only once.
    // This allows external code to manually trigger initialization:
    // section.dispatchEvent(new Event('init-slider'))
    if (!this.initEventAttached) {
      this.initEventAttached = true;
      this.addEventListener('init-slider', () => this.initializeSlider());
    }

    // Attempt automatic initialization on connect.
    // If .slider-track is not in the DOM yet, it will warn and exit gracefully.
    // Dispatch "init-slider" later to retry once the content is ready.
    this.initializeSlider();
  }

  disconnectedCallback() {
    this.removeScrollAndClickListeners();
  }

  attributeChangedCallback(attrName, previousValue, newValue) {
    // Re-apply CSS variables when attributes change after initial setup
    if (this.isSetupComplete && previousValue !== newValue) {
      this.applyCSSVariables();
    }
  }

  initializeSlider() {
    this.applyCSSVariables();

    const scrollTrack = this.querySelector('.slider-track');

    if (!scrollTrack) {
      console.warn(
        '<slider-component>: .slider-track was not found in the DOM.\n' +
        'If your content loads dynamically, dispatch "init-slider" once it is ready:\n' +
        '  element.dispatchEvent(new Event("init-slider"))'
      );
      return;
    }

    // If previously set up, remove old listeners before re-binding
    if (this.isSetupComplete) {
      this.removeScrollAndClickListeners();
    }

    // ── Cache element references
    this.scrollTrack = scrollTrack;
    this.progressFill = this.querySelector('.slider-progress-fill');
    this.sliderControls = this.querySelector('.slider-controls');
    this.prevButton = this.querySelector('.slider-arrow--prev');
    this.nextButton = this.querySelector('.slider-arrow--next');

    // ── Named bound handlers (needed for clean removeEventListener)
    this.handleTrackScroll = () => this.scheduleProgressUpdate();
    this.handlePrevClick = () => this.scrollOneCard('prev');
    this.handleNextClick = () => this.scrollOneCard('next');

    // ── Attach event listeners
    this.scrollTrack.addEventListener('scroll', this.handleTrackScroll, { passive: true });
    this.handleWindowResize = () => this.scheduleProgressUpdate();
    window.addEventListener('resize', this.handleWindowResize, { passive: true });
    this.prevButton?.addEventListener('click', this.handlePrevClick);
    this.nextButton?.addEventListener('click', this.handleNextClick);

    // ── Mark as ready and run first paint
    this.isSetupComplete = true;
    this.pendingAnimationFrame = null;
    this.updateProgressAndArrows();
  }

  removeScrollAndClickListeners() {
    // Cancel any pending animation frame to avoid stale updates
    if (this.pendingAnimationFrame !== null) {
      cancelAnimationFrame(this.pendingAnimationFrame);
      this.pendingAnimationFrame = null;
    }

    this.scrollTrack?.removeEventListener('scroll', this.handleTrackScroll);
    if (this.handleWindowResize) window.removeEventListener('resize', this.handleWindowResize);
    this.prevButton?.removeEventListener('click', this.handlePrevClick);
    this.nextButton?.removeEventListener('click', this.handleNextClick);
  }

  applyCSSVariables() {
    const cardsOnDesktop = this.getAttribute('cards-desktop') || '4';
    const cardsOnMobile = this.getAttribute('cards-mobile') || '1.5';
    const cardGapInPx = this.getAttribute('card-gap-desktop') || '20';
    const cardGapMobileInPx = this.getAttribute('card-gap-mobile') || '10';

    this.style.setProperty('--cards-desktop', cardsOnDesktop);
    this.style.setProperty('--cards-mobile', cardsOnMobile);
    this.style.setProperty('--card-gap-desktop', `${cardGapInPx}px`);
    this.style.setProperty('--card-gap-mobile', `${cardGapMobileInPx}px`);
  }

  scheduleProgressUpdate() {
    // Skip scheduling if a frame update is already queued.
    // This coalesces many rapid scroll events into one DOM write per frame.
    if (this.pendingAnimationFrame !== null) return;

    this.pendingAnimationFrame = requestAnimationFrame(() => {
      this.pendingAnimationFrame = null;
      this.updateProgressAndArrows();
    });
  }

  updateProgressAndArrows() {
    if (!this.scrollTrack) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.scrollTrack;
    const maxScrollableDistance = scrollWidth - clientWidth;

    // Progress bar: show proportional progress based on visible view rather than 0%
    const scrollProgressPercent = scrollWidth > 0
      ? Math.min(100, Math.max(0, ((scrollLeft + clientWidth) / scrollWidth) * 100))
      : 0;

    this.style.setProperty('--progress-fill', `${scrollProgressPercent}%`);

    // Arrow disabled states
    // 1px tolerance handles sub-pixel rounding on HiDPI / fractional zoom
    const isScrolledToStart = scrollLeft <= 1;
    const isScrolledToEnd = maxScrollableDistance <= 1 || scrollLeft >= maxScrollableDistance - 1;

    this.prevButton?.toggleAttribute('disabled', isScrolledToStart);
    this.nextButton?.toggleAttribute('disabled', isScrolledToEnd);

    // Hide controls when all cards fit within one page (per device)
    this.updateControlVisibility();
  }

  updateControlVisibility() {
    if (!this.sliderControls) return;
    const totalCards = this.scrollTrack
      ? Array.from(this.scrollTrack.querySelectorAll('.slider-card')).filter(
          (c) => !c.classList.contains('hidden') && c.offsetWidth > 0
        ).length
      : 0;
    const desktopPerPage = Math.floor(parseFloat(this.getAttribute('cards-desktop') || '4'));
    const mobilePerPage  = Math.floor(parseFloat(this.getAttribute('cards-mobile')  || '1.5'));
    this.sliderControls.classList.toggle('desktop-controls-hidden', totalCards <= desktopPerPage);
    this.sliderControls.classList.toggle('mobile-controls-hidden',  totalCards <= mobilePerPage);
  }

  scrollOneCard(direction) {
    const firstCard = this.scrollTrack?.querySelector('.slider-card');
    if (!firstCard) return;

    const cardWidth = firstCard.offsetWidth;
    const gapBetweenCards = parseFloat(getComputedStyle(this.scrollTrack).columnGap) || 0;
    const scrollStepInPx = cardWidth + gapBetweenCards;
    const scrollDirection = direction === 'prev' ? -scrollStepInPx : scrollStepInPx;

    this.scrollTrack.scrollBy({ left: scrollDirection, behavior: 'smooth' });
  }
}

customElements.define('card-slider', CardSlider);


// --------------------------- SHOP BY PREFERENCES COMPONENT ---------------------------
class ShopByPreferences extends HTMLElement {
  constructor() {
    super();

    this.tabs = this.querySelectorAll('.shop-by-pref__tab-btn');
    this.filterLists = this.querySelectorAll('.shop-by-pref__filter-list');
    this.filters = this.querySelectorAll('.shop-by-pref__filter-list span[data-filter]');
    this.grids = this.querySelectorAll('.shop-by-pref__grid');
    this.progressTrack = this.querySelector('.sbp-progress-track');
    this.progressFill = this.querySelector('.sbp-progress-fill');
    this._rafId = null;

    if (this.tabs.length) this._init();
  }

  _init() {
    // Attach scroll listeners to every grid
    this.grids.forEach((grid) => {
      grid.addEventListener('scroll', () => {
        if (this._rafId) cancelAnimationFrame(this._rafId);
        this._rafId = requestAnimationFrame(() => this._updateProgress(grid));
      }, { passive: true });
    });

    // Activate first tab on load
    const firstTabId = this.tabs[0]?.dataset.tabId;
    if (firstTabId) this._switchTab(firstTabId);

    this.tabs.forEach((btn) => {
      btn.addEventListener('click', () => this._switchTab(btn.dataset.tabId));
    });

    this.filters.forEach((chip) => {
      chip.addEventListener('click', () => this._toggleFilter(chip));
    });

    window.addEventListener('resize', () => {
      const activeGrid = this.querySelector('.shop-by-pref__grid:not(.hidden)');
      if (activeGrid) {
        const hasOverflow = activeGrid.scrollWidth > activeGrid.clientWidth;
        if (this.progressTrack) {
          this.progressTrack.classList.toggle('hidden', !hasOverflow);
        }
        this._updateProgress(activeGrid);
      }
    }, { passive: true });
  }

  // Progress bar: show proportional progress based on visible view rather than 0%
  _updateProgress(grid) {
    if (!this.progressFill || !grid) return;
    const pct = grid.scrollWidth > 0
      ? Math.min(100, Math.max(0, ((grid.scrollLeft + grid.clientWidth) / grid.scrollWidth) * 100))
      : 0;
    this.progressFill.style.width = pct + '%';
  }

  // Called after every tab switch or filter change.
  // Resets scroll to 0, then waits one frame for the DOM to settle
  // before deciding whether the progress bar should be visible and updating progress.
  _refreshProgress(grid) {
    grid.scrollLeft = 0;

    // Wait for hidden/visible class changes to take effect in layout
    requestAnimationFrame(() => {
      const hasOverflow = grid.scrollWidth > grid.clientWidth;
      if (this.progressTrack) {
        this.progressTrack.classList.toggle('hidden', !hasOverflow);
      }
      this._updateProgress(grid);
    });
  }

  // Switch active tab: show correct filter list + grid, then activate first filter
  _switchTab(tabId) {
    // Tab button states
    this.tabs.forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.tabId === tabId);
    });

    // Filter lists: show only matching, clear active states
    this.filterLists.forEach((list) => {
      const isActive = list.dataset.tabId === tabId;
      list.classList.toggle('hidden', !isActive);
      list.querySelectorAll('span[data-filter]').forEach((chip) => {
        chip.classList.remove('active');
      });
    });

    // Grids: show only matching, hide all items initially
    this.grids.forEach((grid) => {
      const isActive = grid.dataset.tabId === tabId;
      grid.classList.toggle('hidden', !isActive);
      if (isActive) {
        grid.querySelectorAll('.shop-by-pref__grid-item').forEach((item) => {
          item.classList.add('hidden');
        });
      }
    });

    // Activate the first filter chip of the newly active tab
    const activeFilterList = this.querySelector(`.shop-by-pref__filter-list[data-tab-id="${tabId}"]`);
    const firstChip = activeFilterList ? activeFilterList.querySelector('span[data-filter]') : null;
    if (firstChip) {
      this._applyFilter(firstChip);
    } else {
      // No filters defined — fall back to showing all items
      const grid = this.querySelector(`.shop-by-pref__grid[data-tab-id="${tabId}"]`);
      if (grid) {
        grid.querySelectorAll('.shop-by-pref__grid-item').forEach((item) => item.classList.remove('hidden'));
        this._refreshProgress(grid);
      }
    }
  }

  // Apply a filter chip (always activates, never deselects to "show all")
  _applyFilter(chip) {
    const tabId = chip.dataset.tabId;
    const filterVal = chip.dataset.filter;

    // Clear all active chips in this tab, then mark this one active
    this.filters.forEach((c) => {
      if (c.dataset.tabId === tabId) c.classList.remove('active');
    });
    chip.classList.add('active');

    const grid = this.querySelector(`.shop-by-pref__grid[data-tab-id="${tabId}"]`);
    if (!grid) return;

    grid.querySelectorAll('.shop-by-pref__grid-item').forEach((item) => {
      const assigned = (item.dataset.assignedFilters || '')
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
      item.classList.toggle('hidden', !assigned.includes(filterVal.trim()));
    });

    this._refreshProgress(grid);
  }

  // Toggle a filter chip — clicking an already-active chip keeps it active
  _toggleFilter(chip) {
    // Always apply the selected filter (no deselect-to-all)
    this._applyFilter(chip);
  }
}

customElements.define('shop-by-preferences', ShopByPreferences);


// --------------------------- TAB SWITCHING SLIDER COMPONENT ---------------------------
class TabSwitchingSlider extends HTMLElement {
  constructor() {
    super();

    this.tabs = this.querySelectorAll('button.tab-label');
    this.filterLists = this.querySelectorAll('.filter-list');
    this.filters = this.querySelectorAll('span[data-filter]');
    this.sliders = this.querySelectorAll('.tab-contents card-slider');

    if (this.tabs.length && this.sliders.length) {
      this._init();
    }
  }

  _init() {
    // Activate the first available tab on load
    const firstTab = this.tabs[0];
    if (firstTab) {
      this._tabSelection(firstTab.dataset.tabId);
    }

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        this._tabSelection(tab.dataset.tabId);
      });
    });

    this.filters.forEach((filter) => {
      filter.addEventListener('click', () => {
        this._filterSelection(filter);
      });
    });
  }

  // ── Tab switch: show correct slider + filter list, activate first filter ──────────
  _tabSelection(tabId) {
    // 1. Update tab button active states
    this.tabs.forEach((tab) => {
      tab.classList.toggle('active', tab.dataset.tabId === tabId);
    });

    // 2. Show / hide filter lists and clear any active filter
    this.filterLists.forEach((list) => {
      const isActive = list.getAttribute('data-tab-id') === tabId;
      list.classList.toggle('hidden', !isActive);

      // Reset every filter span in this list to deselected
      list.querySelectorAll('span[data-filter]').forEach((span) => {
        span.classList.remove('active');
      });
    });

    // 3. Show / hide sliders, hide all cards initially
    this.sliders.forEach((slider) => {
      const isActive = slider.dataset.tabId === tabId;
      slider.classList.toggle('hidden', !isActive);

      if (isActive) {
        // Hide all cards — first filter will reveal only its subset
        slider.querySelectorAll('.slider-card').forEach((card) => {
          card.classList.add('hidden');
        });

        // Scroll the track back to the start
        const track = slider.querySelector('.slider-track');
        if (track) track.scrollLeft = 0;
      }
    });

    // 4. Activate the first filter chip for this tab
    const activeFilterList = this.querySelector(`.filter-list[data-tab-id="${tabId}"]`);
    const firstFilter = activeFilterList ? activeFilterList.querySelector('span[data-filter]') : null;
    if (firstFilter) {
      this._applyFilter(firstFilter);
    } else {
      // No filters defined — fall back to showing all cards
      this._showAllCards(tabId);
    }
  }

  // ── Apply a filter (always activates, never deselects to show all) ─────
  _applyFilter(filter) {
    const tabId = filter.dataset.tabId;
    const filterValue = filter.dataset.filter;

    // Mark only this filter active, deactivate all others in the tab
    this.filters.forEach((item) => {
      if (item.dataset.tabId === tabId) {
        item.classList.toggle('active', item === filter);
      }
    });

    this._filterCards(tabId, filterValue);
  }

  // ── Filter click: clicking an already-active filter keeps it active ────
  _filterSelection(filter) {
    // Always apply the filter — no deselect-to-all behaviour
    this._applyFilter(filter);
  }

  // ── Show all cards for a tab and refresh its slider ───────────────────
  _showAllCards(tabId) {
    const slider = this.querySelector(`.tab-contents card-slider[data-tab-id="${tabId}"]`);
    if (!slider) return;

    slider.querySelectorAll('.slider-card').forEach((card) => {
      card.classList.remove('hidden');
    });

    const track = slider.querySelector('.slider-track');
    if (track) track.scrollLeft = 0;

    this._refreshSlider(slider);
  }

  // ── Filter cards for a tab and refresh its slider ─────────────────────
  _filterCards(tabId, filterValue) {
    const slider = this.querySelector(`.tab-contents card-slider[data-tab-id="${tabId}"]`);
    if (!slider) return;

    slider.querySelectorAll('.slider-card').forEach((card) => {
      const assigned = card.dataset.assignedFilters || '';
      // assignedFilters is comma-separated (set by Liquid replace)
      const values = assigned.split(',').map((v) => v.trim());
      card.classList.toggle('hidden', !values.includes(filterValue.trim()));
    });

    // Scroll back to start so user sees the first filtered card
    const track = slider.querySelector('.slider-track');
    if (track) track.scrollLeft = 0;

    this._refreshSlider(slider);
  }

  // ── Re-initialise a card-slider and hide controls if not needed ───────
  _refreshSlider(slider) {
    // Re-initialise via the existing 'init-slider' event so that
    // arrows & progress bar reflect the currently visible cards
    slider.dispatchEvent(new Event('init-slider'));

    // After a frame (so the DOM has settled) decide whether to hide
    // pagination & arrows
    requestAnimationFrame(() => {
      this._updateSliderControls(slider);
    });
  }

  // ── Hide arrows + progress bar when cards fit within one page ─────────
  _updateSliderControls(slider) {
    const controls = slider.querySelector('.slider-controls');
    const track = slider.querySelector('.slider-track');
    if (!controls || !track) return;

    const visibleCards = Array.from(track.querySelectorAll('.slider-card')).filter(
      (c) => !c.classList.contains('hidden')
    );

    // Determine how many cards fit in one page based on viewport
    const isMobile = window.innerWidth <= 768;
    const perPage = isMobile
      ? parseFloat(slider.getAttribute('cards-mobile') || 1.5)
      : parseFloat(slider.getAttribute('cards-desktop') || 4);

    // Hide controls entirely when all visible cards fit on one page
    const shouldHide = visibleCards.length <= Math.floor(perPage);
    controls.classList.toggle('hidden', shouldHide);
  }
}

customElements.define('tab-switching-slider', TabSwitchingSlider);


// --------------------------- CARD SWATCHES COMPONENT ---------------------------
// class NewCardSwatches extends HTMLElement {
//   connectedCallback() {
//   const moreButton = this.querySelector('.remaining-swatch-count');

//     if (!moreButton) return

//     moreButton.addEventListener('click', () => {
//       this.querySelectorAll('.swatch--hidden').forEach((swatch) => {
//         swatch.classList.remove('swatch--hidden');
//       });

//       moreButton.remove();
//     });
//   }
// }

// customElements.define('new-card-swatches', NewCardSwatches);


// // QUICK ADD BUTTON COMPONENT - PRODUCT CARD

// class QuickAddButton extends HTMLElement {
//   constructor() {
//     super()

//     this.productCard = this.closest(".product-card-wrapper")
//     this.addToCartButton = this.productCard ? this.productCard.querySelector(".card__content .quick-add__submit") : null

//     this.plusSVG = this.querySelector(".plus-svg-icon")
//     this.loader = this.querySelector(".loading__spinner")

//     if (this.addToCartButton) {
//       this.addEventListener("click", () => this._ATCprocess())
//     }
//   }

//   _ATCprocess() {
//     this.addToCartButton.click()

//     this.plusSVG.classList.add("hidden")
//     if (this.loader) this.loader.classList.remove("hidden")
//   }
// }

// customElements.define("quick-add-button", QuickAddButton)